import { captureException } from '@udemy/sentry';
import { udApiStat } from '@udemy/ud-api';
import { trackAmplitudeEvent } from './bootstrap-amplitude-gs';
import { registerDefaultAmplitudeEventMappings } from './register-default-mappings';
import { AmplitudeEvent } from './types/base-events';

/**
 * Minimal structural shape of a TrackingEvent required by transformers.
 * Keeping this local avoids a circular dependency with @udemy/event-tracking
 * while still allowing transformers to access event-specific payload fields.
 */

/**
 * A function that maps a raw TrackingEvent payload to an AmplitudeEvent.
 * Return `null` to suppress the event (e.g. required fields are missing).
 */

/**
 * Wraps an AmplitudeEvent so that additional fields are merged into the
 * properties returned by `toEventProperties()`. Additional fields take
 * precedence, allowing call-site context to extend or selectively override
 * the base event payload.
 */
class EnrichedAmplitudeEvent extends AmplitudeEvent {
  constructor(base, additionalFields) {
    super();
    this.base = base;
    this.additionalFields = additionalFields;
    this.eventName = base.eventName;
    this.category = base.category;
    this.createTime = base.createTime;
    this.sendTime = base.sendTime;
    this.eventId = base.eventId;
  }
  toEventProperties() {
    return {
      ...this.base.toEventProperties(),
      ...this.additionalFields
    };
  }
}

/**
 * A callback compatible with EventTrackingCallback from @udemy/event-tracking.
 * Defined locally so this package does not need to import from event-tracking.
 */

const registry = new Map();
let defaultMappingsRegistered = false;

/**
 * Register a transformer for a given event-tracking event type. This is the
 * single source of truth for which events are forwarded to Amplitude and how
 * their fields are mapped.
 *
 * The transformer receives the raw TrackingEvent payload and returns the
 * `AmplitudeEvent` instance to send, or `null` to suppress the event (e.g.
 * when required fields are missing). Events whose `_type` has no registered
 * entry are silently dropped.
 *
 * Additional fields from the `publishEvent` call site are merged on top of
 * the transformer's output at dispatch time — no registration-time enricher
 * is needed.
 *
 * @example
 * registerAmplitudeEventMapping('CategoryNavItemSelectEvent', (event) => {
 *     const e = event as {ui_region: string; category_id?: number};
 *     return new CategoryNavItemSelectEvent(e.ui_region, e.category_id);
 * });
 */
export function registerAmplitudeEventMapping(eventType, transformer) {
  // Wrap the transformer in a try-catch so that any runtime error inside a
  // transformer is surfaced as a warning (with stack trace) rather than
  // bubbling up and breaking the broader event-tracking pipeline.
  const safeTransformer = event => {
    try {
      return transformer(event);
    } catch (err) {
      captureException(err);
      udApiStat.increment('amplitude.web_sdk.transformer_error', {
        event_type: eventType
      });
      return null;
    }
  };
  registry.set(eventType, safeTransformer);
}

/**
 * Returns a callback suitable for passing to `Tracker.registerCallback()` or
 * as the `callback` argument of `initializeEventTracking`. For each incoming
 * TrackingEvent:
 * 1. Looks up the registered transformer by `_type` — drops the event if none found.
 * 2. Calls the transformer — drops the event if it returns `null`.
 * 3. If `additionalFields` were supplied at the `publishEvent` call site, wraps
 *    the event so those fields are merged into the Amplitude payload (taking
 *    precedence over the transformer's own properties).
 * 4. Forwards the final event to `trackAmplitudeEvent`.
 */
export function createAmplitudeTrackingCallback() {
  // Lazily register default mappings the first time a callback is created so
  // that calling createAmplitudeTrackingCallback() is the only integration
  // point an application needs — no separate registerDefaultAmplitudeEventMappings() call required.
  if (!defaultMappingsRegistered) {
    registerDefaultAmplitudeEventMappings();
    defaultMappingsRegistered = true;
  }
  return (event, additionalFields) => {
    const transformer = registry.get(event._type);
    if (!transformer) {
      return;
    }
    const amplitudeEvent = transformer(event);
    if (!amplitudeEvent) {
      return;
    }
    const eventToSend = additionalFields ? new EnrichedAmplitudeEvent(amplitudeEvent, additionalFields) : amplitudeEvent;
    trackAmplitudeEvent(eventToSend);
  };
}

/**
 * Clear all registered mappings. Intended for use in tests only.
 * @internal
 */
export const _resetRegistryForTesting = /* istanbul ignore next */() => {
  registry.clear();
};
//# sourceMappingURL=event-registry.js.map